import {
  addDays,
  differenceInCalendarMonths,
  differenceInCalendarWeeks,
  format,
  startOfWeek,
  subMonths,
  subWeeks,
} from "date-fns";
import { TransactionView } from "@bitwarden/web-vault/app/models/view/transaction/transaction.view";
import { getTransactionAmount } from "@bitwarden/web-vault/app/shared/utils/helper.transactionView/amount";
import { BudgetPageInterval } from "../budget/budget.utils";
import { SavingGoalContribution, SavingGoalView } from "./saving-goal.view";

const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;
/** Matches BudgetUtils' Monday-anchored week convention (startingPreference 1 = Monday). */
const WEEK_STARTS_ON = 1;

export class SavingGoalUtils {
  /** `currentAmount` is never stored on SavingGoalView (it'd go stale the moment the tracked
   *  account's balance changes) — callers compute it live via `toProgress(goal, rawBalance)`
   *  and pass it in here. */
  static progressPct(goal: SavingGoalView, currentAmount: number): number {
    if (!goal.targetAmount) {
      return 0;
    }
    return Math.min((currentAmount / goal.targetAmount) * 100, 100);
  }

  static remaining(goal: SavingGoalView, currentAmount: number): number {
    return Math.max(goal.targetAmount - currentAmount, 0);
  }

  static isReached(goal: SavingGoalView, currentAmount: number): boolean {
    return currentAmount >= goal.targetAmount;
  }

  static isPercentageMethod(goal: SavingGoalView): boolean {
    return goal.trackingMethod === "percentageOfBalance";
  }

  /** Progress amount for a raw account balance, per the goal's tracking method — the same
   *  transform used for `currentAmount` and for each contribution-history row's start/end.
   *
   *  KNOWN LIMITATION (#5381, decision needed): for growthAboveBaseline, buildContributionHistory
   *  derives a row's `added` as `toProgress(rawEnd) - toProgress(rawStart)`. When both ends sit
   *  below the baseline, both clamp to 0 and a real deposit reports as added=0 - only the portion
   *  of a period that crosses the baseline shows up. Whether `added` should instead report the
   *  raw delta, or the column should be relabelled so the clamp reads as intentional, is a
   *  product call, not made here. */
  static toProgress(goal: SavingGoalView, rawBalance: number): number {
    if (goal.trackingMethod === "percentageOfBalance") {
      return rawBalance * ((goal.percentageOfBalance ?? 0) / 100);
    }
    return Math.max(rawBalance - (goal.baselineToHoldBack ?? 0), 0);
  }

  /** Whole weeks between now and the target date; null when there's no target date or it's past. */
  static weeksLeft(goal: SavingGoalView, now: number = Date.now()): number | null {
    if (!goal.targetDate) {
      return null;
    }
    const target = new Date(goal.targetDate).getTime();
    const diff = target - now;
    return diff > 0 ? Math.ceil(diff / MS_PER_WEEK) : null;
  }

  /** How much needs to be added per week to hit the target on time; null when not computable. */
  static weeklyRateToFinishOnTime(
    goal: SavingGoalView,
    currentAmount: number,
    now: number = Date.now(),
  ): number | null {
    const weeks = SavingGoalUtils.weeksLeft(goal, now);
    if (!weeks) {
      return null;
    }
    return SavingGoalUtils.remaining(goal, currentAmount) / weeks;
  }

  /** Number of calendar periods (months or weeks, per `granularity`) since the goal's startDate,
   *  inclusive of the current period — the real depth of contribution history to build, instead
   *  of a fixed trailing window. */
  static periodsSince(
    startDate: string | null | undefined,
    granularity: BudgetPageInterval = "monthly",
    now: Date = new Date(),
  ): number {
    if (!startDate) {
      return 1;
    }
    const start = new Date(startDate);
    if (granularity === "weekly") {
      return Math.max(
        differenceInCalendarWeeks(now, start, { weekStartsOn: WEEK_STARTS_ON }) + 1,
        1,
      );
    }
    return Math.max(differenceInCalendarMonths(now, start) + 1, 1);
  }

  /**
   * Real per-period contribution history for a tracked account, in *progress* terms rather than
   * raw balance — `toProgress` is the same transform that produces `currentAmount`
   * (`balance - baseline` for growthAboveBaseline, `balance * pct/100` for percentageOfBalance),
   * applied to each period's start/end so the numbers here reconcile with the goal's headline
   * progress figure. Walks backward from the account's real current balance (raw, not the
   * already-transformed progress amount) since that's the one fixed point we know for certain;
   * `added` per period falls out of the delta and needs no separate handling — subtracting a
   * constant baseline or scaling by a percentage both distribute correctly over a delta.
   *
   * `granularity` buckets by calendar month or by Monday-anchored week, matching the budget
   * page's period switcher — switching it re-buckets the same underlying transactions instead
   * of needing separately-stored history per cadence.
   *
   * Uses `getTransactionAmount` (apps/.../shared/utils/helper.transactionView/amount.ts) — the
   * same signed, core-currency amount extractor the real balance-calculation engine uses — via
   * `transaction.postDate` for bucketing.
   */
  static buildContributionHistory(
    transactions: TransactionView[],
    currentRawBalance: number,
    toProgress: (rawBalance: number) => number,
    periodsBack = 3,
    granularity: BudgetPageInterval = "monthly",
    now: Date = new Date(),
  ): SavingGoalContribution[] {
    const isWeekly = granularity === "weekly";
    const bucketKey = (d: Date) =>
      isWeekly
        ? startOfWeek(d, { weekStartsOn: WEEK_STARTS_ON }).getTime().toString()
        : `${d.getFullYear()}-${d.getMonth()}`;
    const periodLabel = (periodStart: Date) =>
      isWeekly
        ? `${format(periodStart, "MMM d")} – ${format(addDays(periodStart, 6), "MMM d, yyyy")}`
        : format(periodStart, "MMMM yyyy");

    const periodStarts: Date[] = [];
    const thisWeekStart = startOfWeek(now, { weekStartsOn: WEEK_STARTS_ON });
    for (let i = periodsBack - 1; i >= 0; i--) {
      periodStarts.push(
        isWeekly ? subWeeks(thisWeekStart, i) : new Date(now.getFullYear(), now.getMonth() - i, 1),
      );
    }

    const rawDeltaByPeriod = new Map<string, number>();
    for (const transaction of transactions) {
      const key = bucketKey(transaction.postDate);
      rawDeltaByPeriod.set(
        key,
        (rawDeltaByPeriod.get(key) ?? 0) + getTransactionAmount(transaction),
      );
    }

    const rows: SavingGoalContribution[] = [];
    let rawEnd = currentRawBalance;
    for (let i = periodStarts.length - 1; i >= 0; i--) {
      const rawAdded = rawDeltaByPeriod.get(bucketKey(periodStarts[i])) ?? 0;
      const rawStart = rawEnd - rawAdded;
      const start = toProgress(rawStart);
      const end = toProgress(rawEnd);
      rows.push({ period: periodLabel(periodStarts[i]), start, end, added: end - start });
      rawEnd = rawStart;
    }
    return rows; // most-recent period first, matching the existing UI order
  }
}
